import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

/**
 * Unified picker that pulls from Internal Adviser Hub.
 * - Shows all active internal advisers in a dropdown.
 * - Falls back to free-text if the user picks "Other…".
 * - `value` is a string (adviser name or free-text).
 */
export default function AdviserPicker({
  value = '',
  onChange,
  placeholder = 'Select internal adviser',
  allowCustom = true,
  allowClear = true,
  testId = 'adviser-picker',
  className = '',
}) {
  const { token: ctxToken } = useAuth();
  const [advisers, setAdvisers] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const t = ctxToken || localStorage.getItem('qindex_token');
    if (!t) { setLoaded(true); return; }
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/internal-advisers?activeOnly=true`;
    fetch(url, { headers: { Authorization: `Bearer ${t}` } })
      .then(r => r.ok ? r.json() : { advisers: [] })
      .then(data => setAdvisers(data?.advisers || []))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [ctxToken]);

  const names = advisers.map(a => a.name);
  const isCustom = value && !names.includes(value);

  const selectValue = !value
    ? '__none__'
    : isCustom
      ? '__custom__'
      : value;

  return (
    <div className={className}>
      <Select
        value={selectValue}
        onValueChange={(v) => {
          if (v === '__none__') onChange('');
          else if (v === '__custom__') onChange(' '); // trigger custom field with non-empty
          else onChange(v);
        }}
      >
        <SelectTrigger data-testid={`${testId}-select`}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {allowClear && <SelectItem value="__none__">— Unassigned —</SelectItem>}
          {loaded && advisers.length === 0 && (
            <SelectItem value="__empty__" disabled>
              No internal advisers — add them in the Internal Adviser Hub
            </SelectItem>
          )}
          {advisers.map(adv => (
            <SelectItem key={adv.id} value={adv.name}>
              {adv.name}{adv.title ? ` · ${adv.title}` : ''}
            </SelectItem>
          ))}
          {allowCustom && <SelectItem value="__custom__">Other (type name)…</SelectItem>}
        </SelectContent>
      </Select>
      {isCustom && allowCustom && (
        <Input
          value={value.trim() === '' ? '' : value}
          onChange={(e) => onChange(e.target.value || ' ')}
          placeholder="Type name"
          className="mt-2"
          data-testid={`${testId}-custom-input`}
        />
      )}
    </div>
  );
}
